import {
  ApplicationConfig,
  ErrorHandler,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core'
import {
  TitleStrategy,
  RouteReuseStrategy,
  provideRouter,
  withComponentInputBinding,
  withNavigationErrorHandler,
} from '@angular/router'
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
  withXsrfConfiguration,
} from '@angular/common/http'
import { MessageService } from 'primeng/api'
import { providePrimeNG } from 'primeng/config'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { provideLingui } from '@tocdk/lingui-angular'
import * as Sentry from '@sentry/angular'
import { TivedoPreset } from './theme/tivedo-preset'
import { providePrimeNgLocale } from './theme/primeng-locale'
import { patchPrimeNgAutoFocus } from './theme/primeng-autofocus'

import { routes } from './app.routes'
import { authInterceptor } from './core/auth/auth.interceptor'
import { RemountOnParamChangeStrategy } from './core/routing/remount-on-param-change.strategy'
import { xsrfInterceptor } from './core/auth/xsrf.interceptor'
import { contentLocaleInterceptor } from './core/http/content-locale.interceptor'
import { draftWriteInterceptor } from './core/http/draft-write.interceptor'
import { supportSessionInterceptor } from './core/auth/support-session.interceptor'
import { seedSupportCredentialFromUrl } from './core/support/support-window.init'
import { initBrand } from './core/branding/brand.initializer'
import { initHostContext } from './core/branding/host-context.service'
import { initFramedMode } from './core/layout/framed-mode'
import { BrandTitleStrategy } from './core/branding/brand-title.strategy'
import {
  createChunkAwareErrorHandler,
  handleNavigationError,
} from './core/error/chunk-reload'

function resolveLocale(): 'en' | 'da' | null {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('tivedo.locale') : null
  if (stored === 'en' || stored === 'da') return stored
  return null
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideAppInitializer(() => seedSupportCredentialFromUrl()),
    // FR-051 U7: what this host is (platform / agency / unknown), read once —
    // BEFORE initBrand, whose bootstrap awaits it (initializers start in order).
    provideAppInitializer(initHostContext),
    // FR-050 U6: brand tokens/chrome applied before the shell renders (AC-9.2).
    provideAppInitializer(initBrand),
    // FR-051 U7 (AC-8.6): <html class="framed"> inside a frame — browser-only, layout-only.
    provideAppInitializer(initFramedMode),
    // #3264 A11: PrimeNG marks every p-button/p-select autofocus="true"; see the patch.
    provideAppInitializer(patchPrimeNgAutoFocus),
    provideRouter(
      routes,
      withComponentInputBinding(),
      // A stale client whose lazy-route chunk 404s after a redeploy (#302)
      // reloads once to pick up the new bundle instead of dead-ending.
      withNavigationErrorHandler((navError) => handleNavigationError(navError.error))
    ),
    provideHttpClient(
      withFetch(),
      // xsrfInterceptor backstops withXsrfConfiguration: Angular's built-in
      // XSRF interceptor skips absolute URLs, and the dev apiBase
      // (http://localhost:3333/api) is absolute — without it, cross-origin
      // mutations reach the API with no X-XSRF-TOKEN and 403.
      withInterceptors([authInterceptor, xsrfInterceptor, contentLocaleInterceptor, supportSessionInterceptor, draftWriteInterceptor]),
      withXsrfConfiguration({
        cookieName: 'XSRF-TOKEN',
        headerName: 'X-XSRF-TOKEN',
      })
    ),
    // FR-050 U6 (AC-5.2): "<page> · <brand>" titles; Tivedo only in standard mode.
    { provide: TitleStrategy, useClass: BrandTitleStrategy },
    // FR-087 U4 F1: record pages flagged `data.remountOnParamChange` remount when only their
    // :id changes (the spotlight jumps record → record); every other route keeps the default.
    { provide: RouteReuseStrategy, useClass: RemountOnParamChangeStrategy },
    provideAnimationsAsync(),
    provideLingui({
      sourceLocale: 'en',
      locales: ['en', 'da'],
      detectLocale: resolveLocale,
      loader: async (locale) => {
        switch (locale) {
          case 'da':
            return import('../locales/da')
          default:
            // English is the source locale: the msgid IS the English string,
            // so no catalog is needed — Lingui falls back to the msgid. (#263)
            return { messages: {} }
        }
      },
    }),
    providePrimeNG({
      theme: {
        preset: TivedoPreset,
        options: {
          darkModeSelector: '.p-dark',
          cssLayer: { name: 'primeng', order: 'theme, base, primeng' },
        },
      },
    }),
    // PrimeNG built-in strings (date picker day/month names, Today/Clear,
    // Monday-first weeks) follow the active Lingui locale. (#2531)
    providePrimeNgLocale(),
    // Compose the Sentry handler: a chunk-load error reaching the global
    // handler (e.g. a non-route dynamic import) reloads once instead of being
    // reported; everything else is forwarded to Sentry unchanged. (#302)
    {
      provide: ErrorHandler,
      useValue: createChunkAwareErrorHandler(Sentry.createErrorHandler()),
    },
    MessageService,
  ],
}
